import React, { useEffect, useState } from 'react';
import { EUserType } from '../data_models/enums';
import { BRAND_ASSETS } from '../config/defaultAssetUrls';
import useCurrentUser from '../hooks/useCurrentUser';
import useRouting from '../hooks/useRouting';

import { IEmployer } from '../data_models/employer';
import { FreelancerToolExperience, FreelancerRole, FreelancerSkillExperience, IFreelancer } from '../data_models/freelancer';
import TextInputField from '../components/common/TextInputField';
import { FilledButton } from '../components/common/Button';
import { DASHBOARD_ROUTE } from '../App';
import { getNumericEnumValues } from '../utils/enumUtils';
import { UserEnumTextMapper } from '../data_models/enumToTextMapper';

import UploadImageComponent from '../components/common/UploadImageComponent';
import RichTextEditor from '../components/common/RichTextEditor';
import Tooltip from '../components/common/Tooltip';
import { OutlinedMultiDropdownMenu } from '../components/common/MultiDropdownMenu';
import MultiTextSelect from '../components/common/MultiTextSelect';
import EditSkillExperience from '../components/profile/common/EditSkillExperience';

const SignupUpdateProfilePage: React.FC = () => {


  const { currentUser, currentUserType, updateCurrentUser } = useCurrentUser();
  const { goToDashboard } = useRouting();
  const [loading, setLoading] = useState<boolean>(false);
  const [updateUserObj, setUpdateUserObj] = useState<IEmployer | IFreelancer | undefined>(undefined);

  const [firstName, setFirstName] = useState<string>();
  const [bio, setBio] = useState((currentUser as IFreelancer)?.bio || '');
  const [currentOccupation, setCurrentOccupation] = useState((currentUser as IFreelancer)?.currentOccupation || '');
  const [roles, setRoles] = useState<FreelancerRole[]>((currentUser as IFreelancer)?.roles || []);
  const [industries, setIndustries] = useState<string[]>((currentUser as IFreelancer)?.industries || []);
  const [skills, setSkills] = useState<FreelancerSkillExperience[]>((currentUser as IFreelancer)?.skills || []);
  const [tools, setTools] = useState<FreelancerToolExperience[]>((currentUser as IFreelancer)?.tools || []);


  useEffect(() => {
    if (!currentUser || !currentUserType) {
      return;
    }
    // Freelancer
    if (currentUserType === EUserType.FREELANCER) {
      if (((currentUser as IFreelancer)?.firstName?.length || 0) > 0) {
        setFirstName((currentUser as IFreelancer)?.firstName);
      }
      setBio((currentUser as IFreelancer).bio || '');
      setCurrentOccupation((currentUser as IFreelancer).currentOccupation || '');
      setRoles((currentUser as IFreelancer).roles || []);
      setIndustries((currentUser as IFreelancer).industries || []);
      setSkills((currentUser as IFreelancer).skills || []);
      setTools((currentUser as IFreelancer).tools || []);
    }

    // Employer
    if (currentUserType === EUserType.EMPLOYER) {
      goToDashboard(); // skip this step for employers for now
    }

  }, [currentUser, currentUserType])


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    if (currentUserType === EUserType.FREELANCER) {
      const updateObj: IFreelancer = {
        bio: bio,
        currentOccupation: currentOccupation,
        roles: roles,
        industries: industries,
        skills: skills,
        tools: tools,
      }
      await updateCurrentUser(updateObj);
      goToDashboard();
    } else if (currentUserType === EUserType.EMPLOYER) {
    }
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Blurred dots */}
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-primary-500/30 blur-3xl" />
      <div className="absolute -top-12 left-64 w-64 h-64 rounded-full bg-primary-700/25 blur-3xl" />
      <div className="absolute -bottom-10 -right-5 w-64 h-64 rounded-full bg-primary-500/20 blur-3xl" />
      <div className="absolute bottom-36 right-5 w-64 h-64 rounded-full bg-secondary-500/20 blur-3xl" />
      <div className="absolute bottom-12 left-12 w-48 h-48 rounded-full bg-primary-200/30 blur-3xl" />

      {/* Content container */}
      <div className="relative z-10 flex flex-col min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-5rem)] max-w-7xl mx-auto">
        <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <img
              alt="Your Company"
              src={BRAND_ASSETS.LOGO_SMALL_BLACK}
              className="mx-auto h-10 w-auto"
            />
            <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
              {`${firstName
                ? `Hi, ${firstName}!`
                : 'Hi!'} Tell us a little more about yourself.`}
            </h2>
          </div>

          {/* Login Form */}
          <div className="mt-10 mx-auto w-full md:max-w-[650px]">

            {/* Form */}
            <div className="md:-mt-5 bg-white px-6 py-12 shadow-md border border-gray-100 md:rounded-lg md:px-12 flex-1">
              <form onSubmit={handleSubmit} className="space-y-4">


                <div>
                  <Tooltip text={'Let us know who you are'}>
                    <label className={`mb-2 block text-sm/6 font-medium text-gray-900 `}>Profile Picture</label>
                  </Tooltip>
                  <UploadImageComponent
                    type="profile-avatar"
                    caption="Image Files less than 20MB"
                  />
                </div>

                <div>
                  <Tooltip text={'Tell us a little about yourself'}>
                    <label className={`mb-2 block text-sm/6 font-medium text-gray-900 `}>Bio</label>
                  </Tooltip>
                  <RichTextEditor
                    value={bio}
                    onChange={(content) => setBio(content)}
                    placeholder="Tell us a little about yourself"
                  />
                </div>

                <div>
                  <Tooltip text={'What do you do for a living?'}>
                    <label className={`mb-2 block text-sm/6 font-medium text-gray-900 `}>Current Occupation</label>
                  </Tooltip>
                  <TextInputField
                    type="text"
                    id="currentOccupation"
                    value={currentOccupation}
                    onChange={(e) => setCurrentOccupation(e.target.value)}
                    placeholder="Enter your current occupation"
                  />
                </div>

                <div>
                  <Tooltip text={'What are the roles you are looking for? Chose "Other" if you do not find your role'}>
                    <label className={`mb-2 block text-sm/6 font-medium text-gray-900 `}>Roles</label>
                  </Tooltip>
                  <OutlinedMultiDropdownMenu
                    options={getNumericEnumValues(FreelancerRole)}
                    optionsToTextMapper={UserEnumTextMapper.freelancerRole}
                    elements={roles}
                    setElements={setRoles}
                    maxNumSelections={3}
                  />
                </div>

                <div>
                  <Tooltip text={'What are the industries in which you have knowledge?'}>
                    <label className={`mb-2 block text-sm/6 font-medium text-gray-900 `}>Industries</label>
                  </Tooltip>
                  <MultiTextSelect
                    elements={industries}
                    setElements={setIndustries}
                  />
                </div>

                <div>
                  <Tooltip text={'Let us know more about your skills and experiences. If your skill is rather a tool, please add it to the tools section.'}>
                    <label className={`mb-2 block text-sm/6 font-medium text-gray-900 `}>Skills</label>
                  </Tooltip>
                  <EditSkillExperience
                    type="skills"
                    data={skills}
                    setData={(newSkills) => setSkills(newSkills as FreelancerSkillExperience[])}
                  />

                </div>

                <div>
                  <Tooltip text={'Let us know more about the tools you are familiar with if they are important for your work.'}>
                    <label className={`mb-2 block text-sm/6 font-medium text-gray-900 `}>Tools</label>
                  </Tooltip>
                  <EditSkillExperience
                    type="tools"
                    data={tools}
                    setData={(newTools) => setTools(newTools as FreelancerToolExperience[])}
                  />
                </div>

                <div className='mt-4' />
                {/* Submit Button */}
                <FilledButton
                  isLoading={loading}
                  fullWidth
                  type='submit'
                >Update Profile
                </FilledButton>
              </form>
            </div>
          </div>

          <p className="mt-10 text-center text-sm/6 text-gray-500">
            <a href={`${DASHBOARD_ROUTE}`} className="font-semibold text-primary-600 hover:text-primary-500">
              Skip
            </a>
            {' '}this step and update profile later
          </p>
        </div>

      </div>
    </div>
  );
};


export default SignupUpdateProfilePage;
